import { useState, useEffect } from 'react';
import { adminApi, type PickupRecord } from '@/lib/admin';
import { useToast } from '@/components/base/Toast';
import { SectionCard, StatusBadge, EmptyState, LoadingState, ErrorState, ConfirmDialog } from '@/pages/admin/components/UI';

const statusMaps = {
  pending: { label: '待审核', className: 'bg-gold-100 text-gold-700', icon: 'ri-time-line' },
  approved: { label: '已通过', className: 'bg-green-100 text-green-600', icon: 'ri-check-line' },
  rejected: { label: '已驳回', className: 'bg-red-100 text-red-500', icon: 'ri-close-line' },
  default: { label: '未知', className: 'bg-brown-100 text-brown-600', icon: 'ri-question-line' },
};

const tabs = [
  { key: 'all', label: '全部' },
  { key: 'pending', label: '待审核' },
  { key: 'approved', label: '已通过' },
  { key: 'rejected', label: '已驳回' },
];

export default function AdminPickups() {
  const { showToast } = useToast();
  const [records, setRecords] = useState<PickupRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [tab, setTab] = useState('all');
  const [actionTarget, setActionTarget] = useState<PickupRecord | null>(null);
  const [actionType, setActionType] = useState<'approve' | 'reject'>('approve');
  const [busy, setBusy] = useState(false);
  const [logisticsNo, setLogisticsNo] = useState('');
  const [fee, setFee] = useState('');
  const [remark, setRemark] = useState('');

  const fetchData = async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const data = await adminApi.listPickups();
      setRecords(data);
    } catch (e) {
      console.error('提货列表加载失败', e);
      setErrorMsg(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAction = async () => {
    if (!actionTarget) return;
    setBusy(true);
    try {
      if (actionType === 'approve') {
        await adminApi.approvePickup(actionTarget.id, {
          logistics_no: logisticsNo || undefined,
          fee: fee ? Number(fee) : undefined,
          remark: remark || undefined,
        });
        showToast(
          `已通过 ${actionTarget.phone || '该用户'} 的提货申请（${actionTarget.total_weight}g）`,
          'success',
        );
      } else {
        await adminApi.rejectPickup(actionTarget.id, remark || undefined);
        showToast('已驳回该提货申请', 'success');
      }
      setActionTarget(null);
      setLogisticsNo('');
      setFee('');
      setRemark('');
      await fetchData();
    } catch (e) {
      showToast(e instanceof Error ? e.message : '操作失败', 'error');
    } finally {
      setBusy(false);
    }
  };

  const filtered = tab === 'all' ? records : records.filter((r) => r.status === tab);
  const pendingCount = records.filter((r) => r.status === 'pending').length;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-brown-900 text-lg font-bold">提货管理</h2>
          <p className="text-brown-400 text-xs mt-0.5">
            {records.length > 0
              ? `共 ${records.length} 笔 · 待审核 ${pendingCount} 笔`
              : '暂无提货申请'}
          </p>
        </div>
        <div className="inline-flex bg-white rounded-lg border border-brown-100 p-1 self-start">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
                tab === t.key ? 'bg-gold-500 text-brown-950' : 'text-brown-500 hover:text-brown-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <SectionCard>
        {loading ? (
          <LoadingState />
        ) : errorMsg ? (
          <ErrorState message={errorMsg} onRetry={fetchData} />
        ) : filtered.length === 0 ? (
          <EmptyState title="暂无提货申请" desc="用户提交提货申请后，待审核的记录会出现在这里" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-brown-400 text-xs border-b border-brown-100">
                  <th className="px-4 md:px-5 py-3 font-medium">用户</th>
                  <th className="px-4 py-3 font-medium">产品</th>
                  <th className="px-4 py-3 font-medium">重量</th>
                  <th className="px-4 py-3 font-medium hidden sm:table-cell">方式</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">地址</th>
                  <th className="px-4 py-3 font-medium">状态</th>
                  <th className="px-4 py-3 font-medium hidden md:table-cell">时间</th>
                  <th className="px-4 py-3 font-medium text-right">操作</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr
                    key={r.id}
                    className="border-b border-brown-50 last:border-0 hover:bg-brown-50/40 transition-colors"
                  >
                    <td className="px-4 md:px-5 py-3.5">
                      <p className="text-brown-900 font-medium">{r.phone || '—'}</p>
                      <p className="text-brown-400 text-xs">#{r.id}</p>
                    </td>
                    <td className="px-4 py-3.5 text-brown-700 text-xs max-w-[160px] truncate">
                      {r.product || '—'}
                    </td>
                    <td className="px-4 py-3.5 text-brown-900 font-bold">{r.total_weight}g</td>
                    <td className="px-4 py-3.5 hidden sm:table-cell">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          r.method === 'store'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}
                      >
                        {r.method === 'store' ? '到店自提' : '快递配送'}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 hidden md:table-cell text-brown-400 text-xs max-w-[200px] truncate">
                      {r.address_detail || '—'}
                    </td>
                    <td className="px-4 py-3.5">
                      <StatusBadge status={r.status} maps={statusMaps} />
                    </td>
                    <td className="px-4 py-3.5 hidden md:table-cell text-brown-500 whitespace-nowrap">
                      {r.create_time || '—'}
                    </td>
                    <td className="px-4 py-3.5 text-right whitespace-nowrap">
                      {r.status === 'pending' ? (
                        <div className="inline-flex items-center gap-2">
                          <button
                            onClick={() => {
                              setActionType('approve');
                              setActionTarget(r);
                              setLogisticsNo('');
                              setFee('');
                              setRemark('');
                            }}
                            className="px-3 py-1.5 rounded-md bg-green-500 text-white text-xs font-medium cursor-pointer whitespace-nowrap hover:bg-green-600 transition-colors"
                          >
                            审核通过
                          </button>
                          <button
                            onClick={() => {
                              setActionType('reject');
                              setActionTarget(r);
                              setRemark('');
                            }}
                            className="px-3 py-1.5 rounded-md border border-brown-200 text-brown-500 text-xs cursor-pointer whitespace-nowrap hover:bg-brown-50 transition-colors"
                          >
                            驳回
                          </button>
                        </div>
                      ) : r.status === 'approved' && r.logistics_no ? (
                        <span className="text-brown-400 text-xs">物流: {r.logistics_no}</span>
                      ) : (
                        <span className="text-brown-300 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* Approval Dialog with extra fields */}
      {actionType === 'approve' && actionTarget && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-5 animate-fade-up">
            <h3 className="text-brown-900 text-base font-bold mb-1">审核通过提货申请</h3>
            <p className="text-brown-400 text-xs mb-4">
              {actionTarget.phone || '该用户'} · {actionTarget.product} · {actionTarget.total_weight}g
              <span className="ml-2">
                {actionTarget.method === 'store' ? '（到店自提）' : '（快递配送）'}
              </span>
            </p>

            <div className="space-y-3 mb-4">
              <div>
                <label className="text-brown-500 text-xs block mb-1">快递/物流单号（选填）</label>
                <input
                  type="text"
                  value={logisticsNo}
                  onChange={(e) => setLogisticsNo(e.target.value)}
                  placeholder="请输入物流单号"
                  className="w-full px-3 py-2 rounded-lg border border-brown-200 text-sm text-brown-900 placeholder:text-brown-300 focus:outline-none focus:border-gold-400"
                />
              </div>
              <div>
                <label className="text-brown-500 text-xs block mb-1">快递费用（元，选填）</label>
                <input
                  type="number"
                  value={fee}
                  onChange={(e) => setFee(e.target.value)}
                  placeholder="0"
                  className="w-full px-3 py-2 rounded-lg border border-brown-200 text-sm text-brown-900 placeholder:text-brown-300 focus:outline-none focus:border-gold-400"
                />
                <p className="text-brown-400 text-[10px] mt-1">审核通过后将从用户可用余额中扣除</p>
              </div>
              <div>
                <label className="text-brown-500 text-xs block mb-1">备注（选填）</label>
                <textarea
                  value={remark}
                  onChange={(e) => setRemark(e.target.value)}
                  placeholder="添加备注信息..."
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border border-brown-200 text-sm text-brown-900 placeholder:text-brown-300 focus:outline-none focus:border-gold-400 resize-none"
                />
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setActionTarget(null);
                  setLogisticsNo('');
                  setFee('');
                  setRemark('');
                }}
                className="flex-1 py-2.5 rounded-lg border border-brown-200 text-brown-700 text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-brown-50 transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleAction}
                disabled={busy}
                className="flex-1 py-2.5 rounded-lg bg-green-500 text-white text-sm font-medium cursor-pointer whitespace-nowrap hover:bg-green-600 transition-colors disabled:opacity-60"
              >
                {busy ? '处理中...' : '确认通过'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Dialog */}
      {actionType === 'reject' && actionTarget && (
        <ConfirmDialog
          open={!!actionTarget}
          title="确认驳回该提货申请？"
          desc={`${actionTarget.phone || '该用户'} 申请提取 ${actionTarget.total_weight}g 黄金，驳回后将恢复为金库中存储状态。`}
          confirmText="确认驳回"
          danger
          loading={busy}
          onCancel={() => setActionTarget(null)}
          onConfirm={handleAction}
        />
      )}
    </div>
  );
}